import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Typography,
    IconButton,
} from "@mui/material";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert, { AlertColor } from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation, gql } from "@apollo/client";

interface AddNewDealerProps {
    open: boolean;
    onClose: () => void;
    onSave: (data: any) => void; // This callback will handle the data mapping
}

const ADD_DEALER_MUTATION = gql`
mutation Mutation($dealerName: String!, $dealerCode: String!, $branchCode: String!, $dealerEmail: String!) {
  addDealer(dealer_name: $dealerName, dealer_code: $dealerCode, branch_code: $branchCode, dealer_email: $dealerEmail) {
    status
    message
  }
}
`;

const initialState = {
    dealerName: "",
    dealerCode: "",
    branchCode: "",
    dealerEmail: "",
    contactNo: ""
};

const AddNewDealers: React.FC<AddNewDealerProps> = ({ open, onClose, onSave }) => {
    const [formData, setFormData] = useState(initialState);
    const [addDealer, { loading, error }] = useMutation(ADD_DEALER_MUTATION);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: AlertColor
    }>({
        open: false,
        message: '',
        severity: 'success'
    });
    const [errors, setErrors] = useState<Record<string, string>>({});

    React.useEffect(() => {
        if (open) {
            setFormData(initialState);
            setSnackbar({ open: false, message: '', severity: 'success' });
            setErrors({});
        }
    }, [open]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        let trimmedValue = value;

        if (name === 'dealerName') {
            trimmedValue = value.replace(/[^a-zA-Z0-9 ]/g, '');
        } else if (name === 'dealerEmail') {
            trimmedValue = value.replace(/[^a-zA-Z0-9@._-]/g, '');
        } else if (name === 'contactNo') {
            // Allow only numbers
            trimmedValue = value.replace(/[^0-9]/g, '');
        } else {
            trimmedValue = value.replace(/[^a-zA-Z0-9]/g, '');
        }

        setFormData((prev) => ({ ...prev, [name]: trimmedValue }));

        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };


    const validateForm = () => {
        const newErrors: Record<string, string> = {};
        let isValid = true;

        const requiredFields = [
            'dealerName', 'dealerCode', 'branchCode', 'dealerEmail','contactNo'
        ];

        requiredFields.forEach(field => {
            if (!formData[field as keyof typeof formData]) {
                newErrors[field] = 'This field is required';
                isValid = false;
            }
        });

        if (formData.dealerEmail && !/^\S+@\S+\.\S+$/.test(formData.dealerEmail)) {
            newErrors.dealerEmail = 'Invalid email format';
            isValid = false;
        }
            if (formData.contactNo && formData.contactNo.length !== 10) {
        newErrors.contactNo = 'Contact No must be 10 digits';
        isValid = false;
    }

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            setSnackbar({ open: true, message: 'Please fix the errors in the form', severity: 'error' });
            return;
        }

        try {
            const { data } = await addDealer({
                variables: {
                    dealerName: formData.dealerName,
                    dealerCode: formData.dealerCode,
                    branchCode: formData.branchCode,
                    dealerEmail: formData.dealerEmail,
                    contactNo: formData.contactNo
                }
            });

            if (data?.addDealer?.status === true) {
                setSnackbar({
                    open: true,
                    message: 'Dealer added successfully!',
                    severity: 'success'
                });

                // Construct a new dealer object for instant UI update
                const newDealer = {
                    dealer_id: Date.now().toString(), // Temporary unique id
                    sapcustomerid: '',
                    dealer_name: formData.dealerName,
                    dealer_code: formData.dealerCode,
                    branch_code: formData.branchCode,
                    dealer_email: formData.dealerEmail,
                    address_line_1: '',
                    gst: '',
                    pan: '',
                    country: '',
                    status: '',
                    br: '',
                    dlr_category: '',
                    dlrtype: '',
                    contactno: '',
                    phone_no1: '',
                    servicezone: '',
                    region: '',
                    city_classification: '',
                    createdate: (Math.floor(Date.now() / 1000)).toString(),
                    sales_number: '',
                    service_number: '',
                    service_email: '',
                    sales_email: ''
                };
                onSave(newDealer);

                setTimeout(() => {
                    onClose();
                }, 1000);
            } else {
                setSnackbar({
                    open: true,
                    message: data?.addDealer?.message || 'Failed to add dealer',
                    severity: 'error'
                });
            }
        } catch (err: any) {
            console.error("Error adding dealer:", err);
            setSnackbar({
                open: true,
                message: err.message || 'Failed to add dealer',
                severity: 'error'
            });
        }
    };

    const handleSnackbarClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const isFormValid = Object.keys(errors).length === 0 &&
        formData.dealerName &&
        formData.dealerCode &&
        formData.branchCode &&
        formData.dealerEmail &&
        formData.contactNo;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 2,
                    width: '100%',
                    maxWidth: '480px',
                }
            }}
        >
            <DialogTitle sx={{
                backgroundColor: '#ffffffff',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 24px'
            }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Add New Dealer
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        color: '#000',
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.1)'
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ padding: '22px', maxHeight: '60vh', overflowY: 'auto' }}>
                <Grid container rowSpacing={2} columnSpacing={2}>
                    {/* Dealer Name */}
                    <Grid sx={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Dealer Name"
                            name="dealerName"
                            value={formData.dealerName}
                            onChange={handleChange}
                            required
                            error={!!errors.dealerName}
                            helperText={errors.dealerName}
                            inputProps={{ maxLength: 20 }}
                        />
                    </Grid>

                    {/* Dealer Code */}
                    <Grid sx={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Dealer Code"
                            name="dealerCode"
                            value={formData.dealerCode}
                            onChange={handleChange}
                            required
                            error={!!errors.dealerCode}
                            helperText={errors.dealerCode}
                            inputProps={{ maxLength: 20 }}
                        />
                    </Grid>

                    {/* Branch Code */}
                    <Grid sx={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Branch Code"
                            name="branchCode"
                            value={formData.branchCode}
                            onChange={handleChange}
                            required
                            error={!!errors.branchCode}
                            helperText={errors.branchCode}
                            inputProps={{ maxLength: 20 }}
                        />
                    </Grid>

                    {/* Dealer Email */}
                    <Grid sx={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Dealer Email"
                            name="dealerEmail"
                            value={formData.dealerEmail}
                            onChange={handleChange}
                            required
                            error={!!errors.dealerEmail}
                            helperText={errors.dealerEmail}
                            inputProps={{ maxLength: 32 }}
                        />
                    </Grid>
                    {/* Contact No */}
                    <Grid sx={{ xs: 12, sm: 6, md: 4 }}>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Contact No"
                            name="contactNo"
                            value={formData.contactNo}
                            onChange={handleChange}
                            required
                            error={!!errors.contactNo}
                            helperText={errors.contactNo}
                            inputProps={{ maxLength: 10 }}
                        />
                    </Grid>             
                </Grid>
            </DialogContent>

            <DialogActions sx={{
                padding: '16px 24px',
                justifyContent: 'flex-end'
            }}>
                <Button
                variant="outlined"
                    onClick={onClose}
                >
                    CANCEL
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!isFormValid || loading}
                    color="error"
                >
                    {loading ? 'Adding...' : 'Add Dealer'}
                </Button>
            </DialogActions>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <MuiAlert
                    onClose={handleSnackbarClose}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                    elevation={6}
                    variant="filled"
                >
                    {snackbar.message}
                </MuiAlert>
            </Snackbar>
        </Dialog>
    );
};

export default AddNewDealers;